import { useState, useEffect, useMemo } from 'react';
import Modal from '../../../../common/_custom/Modal/Modal';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import MultiSelectSearchDropdown from '../../../../common/ManageDealDrawer/Components/MultiSelectSearchDropdown/MultiSelectSearchDropdown';
import { DynamicFieldsRenderer } from '../../../../common/ManageDealDrawer/Components/Sections/DynamicFieldsRenderer';
import { SecurityOfferedItem } from '../../debtProductTypes';
import { SecurityModalData } from '../../useUIStateLogic';
import styles from './SecurityModal.module.scss';
import { AICA_COMPANY_PROFILE, CONFIG_STEP, CONFIG_TYPE } from '../../../../common/ManageDealDrawer/DealDrawerConstants';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { useAppSelector } from '../../../../app/hooks';
import { isEmpty } from '../../../../utils/utils';
import { SECURITY_FORM_KEY } from '../../DebtProductsConstants';

interface SecurityModalProps {
    open: boolean;
    onClose: () => void;
    modalData: SecurityModalData;
    onSave: (data: {
        productId: string;
        lenderId?: string;
        isParentRow: boolean;
        securityOffered: SecurityOfferedItem[];
        copyToAll: boolean;
    }) => void;
}

/**
 * Extracts securityOffered config from AICA_COMPANY_PROFILE config
 * Path: pages -> find step === "DEAL_TERMS" -> config (array) -> config (object) -> securityOffered
 */
const extractSecurityConfig = (configData: any) => {
    const pages = configData?.pages ?? [];
    const dealTermsPage = pages.find((page: any) => page.step === CONFIG_STEP.DEAL_TERMS);
    if (!dealTermsPage) return null;

    // config is an array of sections
    const configSections = dealTermsPage.config ?? [];
    for (const section of configSections) {
        // Each section has a config object with field configurations
        const sectionConfig = section.config ?? {};
        if (sectionConfig.securityOffered) {
            return sectionConfig.securityOffered;
        }
    }
    return null;
};

function SecurityModal({ open, onClose, modalData, onSave }: SecurityModalProps) {
    // Redux state for user data
    const user = useAppSelector((state) => state.user);
    const loggedInUser = user?.userData;
    const platformData = user?.domainMetadata;

    // State for dropdown options fetched from config
    const [securityDropdownOptions, setSecurityDropdownOptions] = useState<any[]>([]);
    const [configLoading, setConfigLoading] = useState<boolean>(false);

    // formData state to hold security data in the format expected by DynamicFieldsRenderer
    const [formData, setFormData] = useState<Record<string, SecurityOfferedItem[]>>({
        [SECURITY_FORM_KEY]: [],
    });
    const [copyToAll, setCopyToAll] = useState<boolean>(false);

    // Fetch security config from AICA_COMPANY_PROFILE
    useEffect(() => {
        if (!open || !platformData || !loggedInUser) return;

        setConfigLoading(true);
        getDealFormConfig(
            {
                accountType: loggedInUser.accountType,
                investorOrgId: loggedInUser.investorOrgId,
                primaryEmail: loggedInUser.primaryEmail,
                configName: AICA_COMPANY_PROFILE,
            },
            {
                onSuccess: (configData: any) => {
                    const securityConfig = extractSecurityConfig(configData);
                    if (securityConfig?.dropdownOptions) {
                        setSecurityDropdownOptions(securityConfig.dropdownOptions);
                    }
                    setConfigLoading(false);
                },
                onError: () => {
                    setConfigLoading(false);
                },
            },
        );
    }, [open, platformData, loggedInUser]);

    // Field config for DynamicFieldsRenderer (using fetched dropdown options)
    const fieldConfig = useMemo(
        () => ({
            type: CONFIG_TYPE.MULTIPLE_SELECT_WITH_INPUT,
            dropdownOptions: securityDropdownOptions,
            preventDuplicateInputs: true,
        }),
        [securityDropdownOptions],
    );

    // Sync formData when modalData changes
    useEffect(() => {
        if (modalData?.securityData) {
            setFormData({
                [SECURITY_FORM_KEY]: modalData.securityData.map((sec) => ({
                    type: sec.type,
                    value: sec.value || '',
                })),
            });
            setCopyToAll(false);
        }
    }, [modalData]);

    if (!modalData) return null;

    const { productId, productName, lenderId, isParentRow, pecSecurity } = modalData;

    // Render tooltip content for mandatory securities
    const renderSecurityTooltip = () => {
        if (!pecSecurity || pecSecurity.length === 0) return null;

        // Filter out empty arrays
        const nonEmptyGroups = pecSecurity.filter((group) => group && group.length > 0);
        if (nonEmptyGroups.length === 0) return null;

        return (
            <div className={styles.SecurityTooltip}>
                <div className={styles.TooltipHeader}>Mandatory Securities</div>
                {nonEmptyGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className={styles.TooltipGroup}>
                        <div className={styles.GroupLabel}>Group {groupIndex + 1}:</div>
                        <ul className={styles.GroupList}>
                            {group.map((item, itemIndex) => (
                                <li key={itemIndex}>{item}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        );
    };

    // Transform options to match MultiSelectSearchDropdown expected format
    const formattedOptions = useMemo(
        () =>
            securityDropdownOptions.map((opt: any) => ({
                label: opt.label,
                value: opt._id || opt.label,
            })),
        [securityDropdownOptions],
    );

    // Get selected items from formData
    const selectedItems = formData[SECURITY_FORM_KEY]?.map((item) => item.type) || [];

    // Handle dropdown selection changes
    const handleToggleSelection = (newSelection: (string | null)[]) => {
        const validSelection = newSelection.filter((item): item is string => item !== null);
        const updatedFormData = validSelection.map((type) => {
            const existing = formData[SECURITY_FORM_KEY]?.find((item) => item.type === type);
            return existing || { type, value: '' };
        });
        setFormData({ [SECURITY_FORM_KEY]: updatedFormData });
    };

    // Handle change from DynamicFieldsRenderer
    const handleChange = (key: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // Check if all selected securities have values filled
    const hasAllValuesFilled = () => {
        const securities = formData[SECURITY_FORM_KEY] || [];
        if (securities.length === 0) return false;
        return true;
    };

    const handleSave = () => {
        const securityOffered = formData[SECURITY_FORM_KEY] || [];

        onSave({
            productId,
            lenderId,
            isParentRow,
            securityOffered,
            copyToAll,
        });

        // Close modal immediately - isRowCalculating will show the loading state
        onClose();
    };

    const handleCancel = () => {
        // Reset state and close
        setFormData({ [SECURITY_FORM_KEY]: [] });
        setCopyToAll(false);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose} size="flexible" persistent={true}>
            <div className={styles.SecurityModal}>
                <div className={styles.Header}>
                    <span>
                        Edit Security <span className={styles.HeaderDivider}>|</span>{' '}
                        <span className={styles.ProductName}>{productName}</span>
                    </span>
                </div>

                <div className={styles.Content}>
                    {configLoading ? (
                        <div className={styles.LoadingState}>
                            <LoadingSpinner color="var(--primary-text-colour)" height="24px" />
                        </div>
                    ) : (
                        <>
                            <div className={styles.FormRow}>
                                <div className={styles.Label}>
                                    <span>Security Offered</span>
                                    {pecSecurity && pecSecurity.some((group) => group && group.length > 0) ? (
                                        <Tooltip
                                            content={renderSecurityTooltip()}
                                            direction="bottom"
                                            hideDelay={400}
                                            style={{
                                                position: 'absolute',
                                                marginBottom: '0.5rem',
                                                marginLeft: '0',
                                                zIndex: 99999,
                                                maxWidth: '400px',
                                                minWidth: '300px',
                                                left: '40px',
                                            }}
                                            contentCss={{
                                                borderRadius: '8px',
                                                position: 'relative',
                                                zIndex: 99999,
                                                boxShadow: '0px 0px 10px 2px #0000001A',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                left: '80px',
                                                width: '300px',
                                            }}
                                        >
                                            <span className={styles.RecommendedLabel}>(Recommended Security ⓘ)</span>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip
                                            content={'-'}
                                            direction="bottom"
                                            hideDelay={400}
                                            fitContent={true}
                                            contentCss={{
                                                marginLeft: '0',
                                                zIndex: 99999,
                                                width: 'fit-content',
                                            }}
                                        >
                                            <span className={styles.RecommendedLabel}>(Recommended Security ⓘ)</span>
                                        </Tooltip>
                                    )}
                                </div>
                                <div className={styles.DropdownWrapper}>
                                    <MultiSelectSearchDropdown
                                        options={formattedOptions}
                                        selectedItems={selectedItems}
                                        toggleSelection={handleToggleSelection}
                                        placeholder="Select Security"
                                        showSelectAll={true}
                                        showFloatingLabel={false}
                                    />
                                </div>
                            </div>

                            {/* Dynamic input fields for each selected security */}
                            <div className={styles.DynamicFieldsWrapper}>
                                <DynamicFieldsRenderer
                                    fieldConfig={fieldConfig}
                                    formKey={SECURITY_FORM_KEY}
                                    formData={formData}
                                    handleChange={handleChange}
                                    dealDrawerState={{ readonly: false }}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.Footer}>
                    {isParentRow && (
                        <label className={styles.CheckboxLabel}>
                            <input
                                type="checkbox"
                                checked={copyToAll}
                                onChange={(e) => setCopyToAll(e.target.checked)}
                                className={styles.Checkbox}
                            />
                            <span>Copy security offered to all selected products and lenders</span>
                        </label>
                    )}

                    <div className={styles.ButtonGroup}>
                        <button className={styles.CancelButton} onClick={handleCancel}>
                            Cancel
                        </button>
                        <button className={styles.SaveButton} onClick={handleSave} disabled={!hasAllValuesFilled()}>
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default SecurityModal;
